import {
    Component,
    inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { cloneDeep } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { RuleErrorContext, RuleSeverityLevel } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Kontrakt } from "~app/features/virkeskop/models/kontrakt-models";
import { AvtalMediatorService } from "~app/features/virkeskop/services/avtal-mediator.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { VirkeskopValidationService } from "~app/features/virkeskop/services/validation/virkeskop-validation.service";
import { ViolversionSelectorComponent } from "../../../violversion-selector/violversion-selector.component";

@Component({
    selector: "rask-viol-editor-popover",
    templateUrl: "./viol-editor-popover.component.html",
    styleUrls: ["./viol-editor-popover.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ViolversionSelectorComponent,
        ActionButtonComponent,
        TextmultilineComponent,
        RouterLink
    ]
})
export class ViolEditorPopoverComponent implements OnInit, OnDestroy {
    protected avtalDirty: boolean;
    protected hasErrors: boolean;
    protected kontrakt: Kontrakt = new Kontrakt();

    private readonly authenticationService = inject(AuthenticationService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly destroy$ = new Subject<void>();
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly virkeskopValidationService = inject(VirkeskopValidationService);

    canCopy(): boolean {
        return !this.avtalDirty && this.hasKopleda();
    }

    canEditViolVersion(): boolean {
        if (this.kontrakt.viol3) {
            // Kan inte välja tillbaka till viol2
            return false;
        }

        if (ObjectUtils.isNullOrUndefined(this.kontrakt.vacsEvents?.skickadTillVacs) &&
            ObjectUtils.isNullOrUndefined(this.kontrakt.vacsEvents?.vacsInlastDatum) &&
            !this.avtalDirty && !this.hasErrors) {
            return true;
        }
        return this.hasVirkesekonomiadministrera() && !this.avtalDirty && !this.hasErrors;
    }

    copyKontrakt() {
        this.avtalMediatorService.copyKontrakt(this.kontrakt.guid);
    }

    copyKontraktClick() {
        this.raskModalService.confirm(new ModalSettings("Kopiera kontrakt", "Är du säker på att du vill kopiera detta kontrakt?"))
            .subscribe({
                next: (response) => {
                    if (response === ConfirmDialogResponse.save) {
                        this.copyKontrakt();
                    }
                },
                error: () => {
                    //
                }
            });
    }

    getRelateratKontraktNamn(): string {
        return `${this.kontrakt.kontraktKopieratFranNummer} - ${this.kontrakt.kontraktKopieratFranBeteckning}`;
    }

    handleNo = () => {
        this.kontrakt = cloneDeep(this.kontraktMediatorService.kontrakt);
    };

    hasVirkesekonomiadministrera(): boolean {
        return this.authenticationService.hasRole(RaskRoller.role.Virkesekonomiadministrera);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.kontraktMediatorService.kontrakt$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(kontrakt => {
            this.kontrakt = cloneDeep(kontrakt);
        });
        this.avtalMediatorService.dirty$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(dirty => {
            this.avtalDirty = dirty;
        });

        this.virkeskopValidationService.visibleMessages$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(messages => {
            const errorMessages = messages.filter(x =>
                x.severity === RuleSeverityLevel.error
                && x.context === RuleErrorContext.save);
            this.hasErrors = errorMessages.length > 0;
        });
    }

    onViolVersionChanged = (event: any) => {
        if (!ObjectUtils.isNullOrUndefined(this.kontrakt.vacsEvents?.skickadTillVacs)) {
            this.openModal("Är du verkligen säker att kontraktet tillåts att byta violversion för VACS?");
        } else {
            this.openModal("Är du säker på att du vill byta violversion för detta kontrakt?");
        }
    };

    private hasKopleda(): boolean {
        return this.authenticationService.hasRole(RaskRoller.role.Kopleda);
    }

    private openModal(text: string) {
        this.raskModalService.confirm(new ModalSettings("Byt violversion",text))
            .subscribe({
                next: (response) => {
                    if (response === ConfirmDialogResponse.save) {
                        this.avtalMediatorService.convertVirkeskopskontrakt(this.kontrakt);
                    }
                },
                error: () => { this.handleNo(); }
            });
    }
}
